import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { AnalysisReport } from "@/components/AnalysisReport";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getAnalysis, createShare, revokeShare, getBilling, getShareStats } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Share2, Printer, Trash2, Eye, MousePointerClick } from "lucide-react";
import { SocialShareButtons, buildAnalysisShareText } from "@/components/SocialShareButtons";

export default function AnalysisDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [billing, setBilling] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [data, b] = await Promise.all([getAnalysis(id), getBilling()]);
        setA(data);
        setBilling(b);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load analysis");
      }
    })();
  }, [id]);

  const handleShare = async () => {
    setCreatingShare(true);
    try {
      const { token } = await createShare(id);
      const url = `${window.location.origin}/share/${token}`;
      setShareUrl(url);
      setShareToken(token);
      setShareOpen(true);
      // fetch stats in the background
      getShareStats(token).then(setShareStats).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setCreatingShare(false);
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeShare(id);
      setShareUrl(null);
      setShareToken(null);
      setShareStats(null);
      setShareOpen(false);
      toast.success("Share link revoked");
    } catch (e) {
      toast.error("Could not revoke");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <SiteHeader />
        <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
          <div className="text-[#a8492a]">{err}</div>
          <Link to="/dashboard" className="text-sm underline">Back to dashboard</Link>
        </main>
      </div>
    );
  }
  if (!a) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <SiteHeader />
        <main className="max-w-5xl mx-auto px-6 md:px-8 py-10 text-[#1E392A]/60">Loading…</main>
      </div>
    );
  }

  const canShare = billing?.can_share;
  const pdfAllowed = billing?.can_white_label;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="no-print">
        <SiteHeader />
      </div>
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="no-print flex items-center justify-between mb-4 flex-wrap gap-2">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-[#1E392A]/60 hover:text-[#1E392A]" data-testid="back-link">
            <ChevronLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              disabled={!canShare || creatingShare}
              data-testid="share-btn"
              className="rounded-full"
              title={canShare ? "Create a shareable read-only link" : "Sharing requires a paid plan"}
            >
              <Share2 className="w-4 h-4 mr-1.5" /> {creatingShare ? "Creating…" : "Share"}
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              disabled={!pdfAllowed}
              data-testid="pdf-btn"
              className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white"
              title={pdfAllowed ? "Print or save as PDF" : "PDF export requires the Agency plan ($79/mo)"}
            >
              <Printer className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>

        <AnalysisReport analysis={a} branding={null} />

        {!pdfAllowed && (
          <div className="no-print mt-8 rounded-2xl bg-white border border-dashed border-[#1E392A]/15 p-5 text-sm text-[#1E392A]/70 flex items-center justify-between flex-wrap gap-3">
            <div>
              <b>White-label PDFs are on the Agency plan ($79/mo).</b> Add your logo, color, and company name to every shared report.
            </div>
            <Link to="/billing" className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white text-sm px-4 py-2" data-testid="upsell-agency-link">
              See Agency plan
            </Link>
          </div>
        )}
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share this analysis</DialogTitle>
            <DialogDescription>
              Anyone with this link can view the report. They don't need an account.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Input readOnly value={shareUrl || ""} data-testid="share-url-input" className="w-full" />
          </div>

          <div className="mt-3">
            <div className="text-xs text-[#1E392A]/55 mb-2">Share it</div>
            <SocialShareButtons
              url={shareUrl}
              text={buildAnalysisShareText(a, { ownerView: true })}
            />
          </div>

          {shareStats && (
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid="share-stats">
              <div className="rounded-xl bg-[#F7F5F0] px-3 py-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#1E392A]/60" />
                <div className="text-xs">
                  <div className="text-[#1E392A]/55">Views</div>
                  <div className="font-display text-lg leading-none mt-0.5">{shareStats.views || 0}</div>
                </div>
              </div>
              <div className="rounded-xl bg-[#F7F5F0] px-3 py-2 flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-[#1E392A]/60" />
                <div className="text-xs">
                  <div className="text-[#1E392A]/55">Click-throughs</div>
                  <div className="font-display text-lg leading-none mt-0.5">{shareStats.referral_clicks || 0}</div>
                </div>
              </div>
            </div>
          )}

          {shareStats?.referral_clicks_by_network && Object.keys(shareStats.referral_clicks_by_network).length > 0 && (
            <div className="mt-3" data-testid="share-stats-by-network">
              <div className="text-[10px] uppercase tracking-wider text-[#1E392A]/45 mb-1.5">By network</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(shareStats.referral_clicks_by_network)
                  .sort((a, b) => b[1] - a[1])
                  .map(([net, n]) => {
                    const label = net === "x" ? "X" : net === "linkedin" ? "LinkedIn" : net === "copy" ? "Copy" : "Direct";
                    return (
                      <span
                        key={net}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F5F0] border border-black/5 px-2.5 py-1 text-xs text-[#1E392A]/80"
                        data-testid={`share-network-${net}`}
                      >
                        <span className="font-medium">{label}</span>
                        <span className="text-[#1E392A]/55">{n}</span>
                      </span>
                    );
                  })}
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mt-4">
            <Button variant="ghost" onClick={handleRevoke} data-testid="revoke-share-btn" className="text-[#a8492a]">
              <Trash2 className="w-4 h-4 mr-1.5" /> Revoke link
            </Button>
            {!billing?.can_white_label && (
              <Link to="/billing" className="text-sm underline text-[#1E392A]/70" data-testid="dialog-upgrade-link">
                Want your logo on this? Upgrade to Agency.
              </Link>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
